import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  CalendarIcon,
  CameraIcon,
  DocumentTextIcon,
  EllipsisVerticalIcon,
  GlobeAltIcon,
  InformationCircleIcon,
  LockClosedIcon,
  MegaphoneIcon,
  PhotoIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { server } from '../../server';
import RoomAddMemberForm from './RoomAddMemberForm';
import MyRoomPostWidgetRedux from '../../widgets/MyRoomPostWidgetRedux';
import RoomMediaGrid from './RoomMediaGrid';
import RoomPostsFeed from './RoomPostsFeed';
import EventCarousel from '../Events/EventCarousel';
import MixedSponsoredAds from '../Sponsored/MixedSponsoredAds';
import { getStoredAuthToken } from '../../utils/authSession';
import ImageCropperModal from '../Image/ImageCropperModal';
import SharedRightSlideout from '../Layout/SharedRightSlideout';
import {
  IMAGE_ACCEPT_WITH_HEIC,
  getImagePreparationErrorMessage,
  prepareImageFileForUpload,
} from '../../utils/imageUploadPreparation';

const FALLBACK_AVATAR = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2280%22 height=%2280%22 viewBox=%220 0 80 80%22%3E%3Crect width=%2280%22 height=%2280%22 rx=%2240%22 fill=%22%2323292f%22/%3E%3Ccircle cx=%2240%22 cy=%2231%22 r=%2214%22 fill=%22%236ee000%22/%3E%3Cpath d=%22M17 70c4-16 16-25 23-25s19 9 23 25%22 fill=%22%236ee000%22 opacity=%22.75%22/%3E%3C/svg%3E';

const getStoredCurrentProfile = () => sessionStorage.getItem('currentProfile') || 'User';

const classNames = (...classes) => classes.filter(Boolean).join(' ');

const getMemberRoleLabel = (member) => member?.role || 'Member';

const getMemberProfilePath = (member) => {
  if (!member?.handle) {
    return '';
  }

  return member.type === 'Shop' ? `/shop/${member.handle}` : `/user/${member.handle}`;
};

const RoomDetails = () => {
  const { handle } = useParams();
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddMemberForm, setShowAddMemberForm] = useState(false); 
  const [activeTab, setActiveTab] = useState('Posts');
  const [postsRefreshKey, setPostsRefreshKey] = useState(0);
  const [avatarUploadError, setAvatarUploadError] = useState('');
  const [isPreparingAvatar, setIsPreparingAvatar] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [bannerUploadError, setBannerUploadError] = useState('');
  const [isPreparingBanner, setIsPreparingBanner] = useState(false);
  const [isUploadingBanner, setIsUploadingBanner] = useState(false);
  const [selectedAvatarFile, setSelectedAvatarFile] = useState(null);
  const [selectedBannerFile, setSelectedBannerFile] = useState(null);
  const [showAvatarCropper, setShowAvatarCropper] = useState(false);
  const [showBannerCropper, setShowBannerCropper] = useState(false);
  const [joinActionLoading, setJoinActionLoading] = useState(false);
  const [joinActionError, setJoinActionError] = useState('');
  const [joinRequestActionId, setJoinRequestActionId] = useState('');
  const [isCommunityManageTrayOpen, setIsCommunityManageTrayOpen] = useState(false);
  const avatarInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const currentProfile = useSelector((state) => state.user.currentProfile) || getStoredCurrentProfile();

  const getRequestHeaders = useCallback(() => {
    const token = getStoredAuthToken();
    const headers = { 'current-profile': currentProfile || getStoredCurrentProfile() };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }, [currentProfile]);

  const fetchRoom = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.get(`${server}/room/rooms/${handle}`, {
        withCredentials: true,
        headers: getRequestHeaders(),
      });
      setRoom(response.data.room);
    } catch (err) {
      setRoom(null);
      setError(err.response?.data?.message || 'Failed to fetch room details');
    } finally {
      setLoading(false);
    }
  }, [getRequestHeaders, handle]);

  useEffect(() => {
    fetchRoom();
  }, [fetchRoom]);

  const handleMemberAdded = () => {
    setShowAddMemberForm(false);
    fetchRoom();
  };

  const handleAvatarFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    setAvatarUploadError('');
    setIsPreparingAvatar(true);

    try {
      const preparedFile = await prepareImageFileForUpload(file);
      setSelectedAvatarFile(preparedFile);
      setShowAvatarCropper(true);
    } catch (error) {
      console.error('Error preparing community avatar:', error);
      setSelectedAvatarFile(null);
      setShowAvatarCropper(false);
      setAvatarUploadError(getImagePreparationErrorMessage(error));
    } finally {
      setIsPreparingAvatar(false);
    }
  };

  const handleBannerFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    setBannerUploadError('');
    setIsPreparingBanner(true);

    try {
      const preparedFile = await prepareImageFileForUpload(file);
      setSelectedBannerFile(preparedFile);
      setShowBannerCropper(true);
    } catch (error) {
      console.error('Error preparing community hero background:', error);
      setSelectedBannerFile(null);
      setShowBannerCropper(false);
      setBannerUploadError(getImagePreparationErrorMessage(error));
    } finally {
      setIsPreparingBanner(false);
    }
  };

  const handleJoinCommunity = async () => {
    if (!room?._id || joinActionLoading || room.isViewerMember || room.isViewerOwner) {
      return;
    }

    setJoinActionError('');
    setJoinActionLoading(true);

    try {
      const response = await axios.post(`${server}/room/rooms/${room._id}/join`, {}, {
        withCredentials: true,
        headers: getRequestHeaders(),
      });
      const updatedRoom = response.data.room || {};

      setRoom((currentRoom) => ({
        ...currentRoom,
        ...updatedRoom,
        members: currentRoom.members,
        isViewerMember: response.data.status === 'joined' || updatedRoom.isViewerMember,
        hasViewerJoinRequest: response.data.status === 'requested' || updatedRoom.hasViewerJoinRequest,
        canPost: response.data.status === 'joined' || updatedRoom.canPost || currentRoom.canPost,
      }));
    } catch (error) {
      setJoinActionError(error.response?.data?.message || 'Could not join this community.');
    } finally {
      setJoinActionLoading(false);
    }
  };

  const handleJoinRequestAction = async (requestId, action) => {
    if (!room?._id || !requestId || joinRequestActionId) {
      return;
    }

    setJoinActionError('');
    setJoinRequestActionId(`${requestId}:${action}`);

    try {
      await axios.patch(`${server}/room/rooms/${room._id}/join-requests/${requestId}`, { action }, {
        withCredentials: true,
        headers: getRequestHeaders(),
      });
      await fetchRoom();
    } catch (error) {
      setJoinActionError(error.response?.data?.message || 'Could not update this join request.');
    } finally {
      setJoinRequestActionId('');
    }
  };

  const handleCroppedAvatar = async (croppedFile) => {
    if (!room?._id) {
      return;
    }

    setAvatarUploadError('');
    setIsUploadingAvatar(true);

    const formData = new FormData();
    formData.append('avatarImage', croppedFile);

    try {
      const response = await axios.patch(`${server}/room/rooms/${room._id}/avatar`, formData, {
        withCredentials: true,
        headers: getRequestHeaders(),
      });

      setRoom((currentRoom) => ({
        ...currentRoom,
        avatar: response.data.avatar || currentRoom.avatar,
      }));
      setShowAvatarCropper(false);
      setSelectedAvatarFile(null);
    } catch (error) {
      console.error('Error updating community avatar:', error);
      setAvatarUploadError(error.response?.data?.message || 'Could not update community logo.');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleCroppedBanner = async (croppedFile) => {
    if (!room?._id) {
      return;
    }

    setBannerUploadError('');
    setIsUploadingBanner(true);

    const formData = new FormData();
    formData.append('bannerImage', croppedFile);

    try {
      const response = await axios.patch(`${server}/room/rooms/${room._id}/banner`, formData, {
        withCredentials: true,
        headers: getRequestHeaders(),
      });

      setRoom((currentRoom) => ({
        ...currentRoom,
        banner: response.data.banner || currentRoom.banner,
      }));
      setShowBannerCropper(false);
      setSelectedBannerFile(null);
    } catch (error) {
      console.error('Error updating community hero background:', error);
      setBannerUploadError(error.response?.data?.message || 'Could not update community hero background.');
    } finally {
      setIsUploadingBanner(false);
    }
  };

  const tabs = useMemo(() => ([
    { name: 'Posts', icon: DocumentTextIcon },
    { name: 'Photos', icon: PhotoIcon },
    { name: 'Members', icon: UsersIcon },
    { name: 'About', icon: InformationCircleIcon },
  ]), []);

  if (loading) {
    return <div className="community-profile-empty-state">Loading community...</div>;
  }

  if (error) {
    return <div className="community-profile-empty-state">{error}</div>;
  }

  if (!room) {
    return <div className="community-profile-empty-state">Community not found.</div>;
  }

  const members = Array.isArray(room.members) ? room.members : [];
  const joinRequests = Array.isArray(room.joinRequests) ? room.joinRequests : [];
  const memberInvites = Array.isArray(room.memberInvites) ? room.memberInvites : [];
  const memberCount = typeof room.memberCount === 'number' ? room.memberCount : members.length;
  const isPrivate = room.privacy === 'private';
  const PrivacyIcon = isPrivate ? LockClosedIcon : GlobeAltIcon;
  const canManageMembers = Boolean(room.isViewerOwner || room.isViewerMember);
  const canEditCommunityAvatar = Boolean(room.isViewerOwner);
  const canManageCommunity = Boolean(room.isViewerOwner);
  const canEditCommunityBanner = Boolean(room.isViewerOwner);
  const communityAvatarUrl = room.avatar?.url || FALLBACK_AVATAR;
  const showJoinAction = Boolean(!room.isViewerOwner && !room.isViewerMember && (room.canJoin || room.canRequestJoin || room.accessDenied || room.hasViewerJoinRequest));
  const joinActionLabel = room.hasViewerJoinRequest
    ? 'Request Sent'
    : isPrivate
      ? 'Request to Join'
      : 'Join Community';

  return (
    <div className="room-details-page community-profile-shell">
      <div className="community-profile-center">
        <section
          className="community-profile-hero"
          style={room.banner?.url ? { backgroundImage: `url(${room.banner.url})` } : undefined}
        >
          {canManageCommunity && (
            <div className="community-profile-hero-actions">
              <button
                type="button"
                className="community-profile-hero-action-button"
                aria-label="Manage community"
                onClick={() => setIsCommunityManageTrayOpen(true)}
              >
                <EllipsisVerticalIcon aria-hidden="true" />
              </button>
            </div>
          )}
          <div className="community-profile-hero-overlay">
            <div className="community-profile-identity">
              <div className="community-profile-avatar-frame">
                <img src={communityAvatarUrl} alt={`${room.name} community logo`} />
                {canEditCommunityAvatar && (
                  <>
                    <button
                      type="button"
                      className="community-profile-avatar-edit-button"
                      onClick={() => avatarInputRef.current?.click()}
                      disabled={isPreparingAvatar || isUploadingAvatar}
                      aria-label="Change community logo"
                    >
                      <CameraIcon aria-hidden="true" />
                    </button>
                    <input
                      ref={avatarInputRef}
                      type="file"
                      accept={IMAGE_ACCEPT_WITH_HEIC}
                      className="community-profile-avatar-input"
                      onChange={handleAvatarFileChange}
                      disabled={isPreparingAvatar || isUploadingAvatar}
                    />
                    <input
                      ref={bannerInputRef}
                      type="file"
                      accept={IMAGE_ACCEPT_WITH_HEIC}
                      className="community-profile-avatar-input"
                      onChange={handleBannerFileChange}
                      disabled={isPreparingBanner || isUploadingBanner}
                    />
                  </>
                )}
              </div>
              <p className="community-profile-kicker">Community</p>
              <div className="community-profile-title-row">
                <h1>{room.name}</h1>
                <span className={`community-profile-privacy-pill ${isPrivate ? 'private' : 'public'}`}>
                  <PrivacyIcon aria-hidden="true" />
                  {isPrivate ? 'Private' : 'Public'}
                </span>
              </div>
              {room.description && <p className="community-profile-description">{room.description}</p>}
              <div className="community-profile-stats">
                <span>{memberCount} Members</span>
                <span>{room.canPost ? 'Posting enabled' : 'Read-only for this profile'}</span>
              </div>
              {showJoinAction && (
                <button
                  type="button"
                  className="community-profile-join-button"
                  onClick={handleJoinCommunity}
                  disabled={joinActionLoading || room.hasViewerJoinRequest || (!room.canJoin && !room.canRequestJoin)}
                >
                  {joinActionLoading ? 'Sending...' : joinActionLabel}
                </button>
              )}
              {joinActionError && <p className="community-profile-avatar-error">{joinActionError}</p>}
              {avatarUploadError && <p className="community-profile-avatar-error">{avatarUploadError}</p>}
              {bannerUploadError && <p className="community-profile-avatar-error">{bannerUploadError}</p>}
              {(isPreparingAvatar || isUploadingAvatar) && (
                <p className="community-profile-avatar-status">
                  {isPreparingAvatar ? 'Preparing logo...' : 'Updating logo...'}
                </p>
              )}
              {(isPreparingBanner || isUploadingBanner) && (
                <p className="community-profile-avatar-status">
                  {isPreparingBanner ? 'Preparing hero background...' : 'Updating hero background...'}
                </p>
              )}
            </div>
          </div>
        </section>

        <div className="community-profile-content-region">
          <aside className="community-profile-side-rail community-profile-left-rail">
          <section className="community-profile-bento-card community-profile-members-summary-widget">
            <div className="community-profile-bento-header">
              <UsersIcon aria-hidden="true" />
              <h3>Members</h3>
            </div>
            <div className="community-profile-member-stack">
              {members.slice(0, 5).map((member) => (
                <img
                  key={`${member.type}-${member._id}`}
                  className="community-profile-member-avatar"
                  src={member.avatar || FALLBACK_AVATAR}
                  alt={member.name || 'Member'}
                />
              ))}
            </div>
            <strong>{memberCount}</strong>
            {canManageMembers && (
              <button
                className="add-member-button community-profile-add-member-button"
                onClick={() => setShowAddMemberForm(true)}
              >
                Add Members
              </button>
            )}
          </section>

          <section className="community-profile-bento-card community-profile-events-widget">
            <div className="community-profile-bento-header">
              <CalendarIcon aria-hidden="true" />
              <h3>Events</h3>
            </div>
            <EventCarousel />
          </section>
          </aside>

          <main className="community-profile-main-column">
          {room.canPost ? (
            <section id="community-post-composer" className="community-profile-bento-card community-profile-composer-card">
              <MyRoomPostWidgetRedux
                roomId={room._id}
                onPostCreated={() => setPostsRefreshKey((key) => key + 1)}
              />
            </section>
          ) : (
            <div className="community-profile-empty-state community-profile-bento-card">
              Join or switch to an invited profile to post here.
            </div>
          )}

          <div className="community-profile-tabs-wrapper community-profile-tabs-bento">
            <nav className="community-profile-tabs" aria-label="Community tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.name}
                  type="button"
                  onClick={() => setActiveTab(tab.name)}
                  className={classNames(
                    activeTab === tab.name ? 'community-profile-tab-active' : 'community-profile-tab-inactive',
                    'community-profile-tab-button'
                  )}
                >
                  <tab.icon className="community-profile-tab-icon" aria-hidden="true" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          <section className="community-profile-bento-card community-profile-tab-content">
            {activeTab === 'Posts' && (
              <RoomPostsFeed roomId={room._id} refreshKey={postsRefreshKey} />
            )}

            {activeTab === 'Photos' && (
              <RoomMediaGrid roomId={room._id} refreshKey={postsRefreshKey} />
            )}

            {activeTab === 'Members' && (
              <div className="community-profile-members-panel">
                {room.isViewerOwner && joinRequests.length > 0 && (
                  <div className="community-profile-join-requests-panel">
                    <h4>Requests to Join</h4>
                    <div className="community-profile-join-request-list">
                      {joinRequests.map((joinRequest) => {
                        const approving = joinRequestActionId === `${joinRequest._id}:approve`;
                        const declining = joinRequestActionId === `${joinRequest._id}:decline`;

                        return (
                          <div key={joinRequest._id} className="community-profile-join-request-card">
                            <img src={joinRequest.avatar || FALLBACK_AVATAR} alt={joinRequest.name || 'Member'} />
                            <div>
                              <strong>{joinRequest.name || 'Member'}</strong>
                              {joinRequest.handle && <small>@{joinRequest.handle}</small>}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleJoinRequestAction(joinRequest._id, 'approve')}
                              disabled={Boolean(joinRequestActionId)}
                            >
                              {approving ? 'Approving...' : 'Approve'}
                            </button>
                            <button
                              type="button"
                              className="community-profile-join-request-decline"
                              onClick={() => handleJoinRequestAction(joinRequest._id, 'decline')}
                              disabled={Boolean(joinRequestActionId)}
                            >
                              {declining ? 'Declining...' : 'Decline'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {canManageMembers && memberInvites.length > 0 && (
                  <div className="community-profile-join-requests-panel">
                    <h4>Pending Invitations</h4>
                    <div className="community-profile-join-request-list">
                      {memberInvites.map((memberInvite) => (
                        <div key={memberInvite._id} className="community-profile-join-request-card community-profile-member-invite-card">
                          <img src={memberInvite.avatar || FALLBACK_AVATAR} alt={memberInvite.name || 'Member'} />
                          <div>
                            <strong>{memberInvite.name || 'Member'}</strong>
                            {memberInvite.handle && <small>@{memberInvite.handle}</small>}
                          </div>
                          <span>Invited</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="community-profile-member-grid">
                  {members.map((member) => {
                    const memberProfilePath = getMemberProfilePath(member);
                    const memberCardContent = (
                      <>
                        <img src={member.avatar || FALLBACK_AVATAR} alt={member.name || 'Member'} />
                        <span>{member.name || 'Member'}</span>
                        <small>{getMemberRoleLabel(member)}</small>
                      </>
                    );

                    return memberProfilePath ? (
                      <Link
                        key={`${member.type}-${member._id}`}
                        to={memberProfilePath}
                        className="community-profile-member-card community-profile-member-card-link"
                      >
                        {memberCardContent}
                      </Link>
                    ) : (
                      <div key={`${member.type}-${member._id}`} className="community-profile-member-card">
                        {memberCardContent}
                      </div>
                    );
                  })}
                </div>
                {members.length === 0 && <div className="community-profile-empty-state">No members yet.</div>}
              </div>
            )}

            {activeTab === 'About' && (
              <div className="community-profile-about-card">
                <div>
                  <span>Access</span>
                  <strong>{isPrivate ? 'Private community' : 'Public community'}</strong>
                </div>
                <div>
                  <span>Members</span>
                  <strong>{members.length}</strong>
                </div>
                <p>{room.description || 'No community description yet.'}</p>
              </div>
            )}
          </section>
          </main>

          <aside className="community-profile-side-rail community-profile-right-rail">
          <section className="community-profile-bento-card community-profile-sponsored-widget">
            <div className="community-profile-bento-header">
              <MegaphoneIcon aria-hidden="true" />
              <h3>Sponsored</h3>
            </div>
            <MixedSponsoredAds />
          </section>
          </aside>
        </div>
      </div>

      {/* Conditionally render the AddMemberForm */}
      {showAddMemberForm && (
        <RoomAddMemberForm
          roomId={room._id}
          currentMembers={room.members}
          currentInvites={memberInvites}
          onMemberAdded={handleMemberAdded}
          onClose={() => setShowAddMemberForm(false)} 
        />
      )}

      {showAvatarCropper && selectedAvatarFile && (
        <ImageCropperModal
          initialImage={selectedAvatarFile}
          updateAvatar={handleCroppedAvatar}
          closeModal={() => setShowAvatarCropper(false)}
          aspectRatio={1}
          minWidth={250}
          isAvatar
          isPost={false}
          isLogo={false}
        />
      )}

      {showBannerCropper && selectedBannerFile && (
        <ImageCropperModal
          initialImage={selectedBannerFile}
          updateAvatar={handleCroppedBanner}
          closeModal={() => setShowBannerCropper(false)}
          aspectRatio={4 / 1}
          minWidth={250}
          isAvatar={false}
          isPost={false}
          isLogo={false}
        />
      )}

      {canManageCommunity && (
        <SharedRightSlideout
          isOpen={isCommunityManageTrayOpen}
          onClose={() => setIsCommunityManageTrayOpen(false)}
          title="Manage Community"
          subtitle="Use the available community controls for this room."
          className="community-manage-shared-slideout"
          bodyClassName="community-manage-shared-slideout-body"
        >
          <div className="community-manage-tray-actions">
            <button
              type="button"
              onClick={() => {
                setIsCommunityManageTrayOpen(false);
                avatarInputRef.current?.click();
              }}
            >
              Edit Community Logo
            </button>
            {canEditCommunityBanner && (
              <button
                type="button"
                onClick={() => {
                  setIsCommunityManageTrayOpen(false);
                  bannerInputRef.current?.click();
                }}
              >
                Edit Hero Background
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setIsCommunityManageTrayOpen(false);
                setShowAddMemberForm(true);
              }}
            >
              Invite Members
            </button>
            <button
              type="button"
              onClick={() => {
                setIsCommunityManageTrayOpen(false);
                fetchRoom();
              }}
            >
              Refresh Community
            </button>
            <button
              type="button"
              className="community-manage-tray-cancel"
              onClick={() => setIsCommunityManageTrayOpen(false)}
            >
              Cancel
            </button>
          </div>
        </SharedRightSlideout>
      )}
      
    </div>
  );
};

export default RoomDetails;




